import logger from '_/services/logger';

import {
  getPictureTargetSize,
  getSignatureTargetSize,
  PICTURE_JPEG_QUALITY,
} from './checkinImageSizes';

type Size = { width: number; height: number };

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to load image'));
    image.src = src;
  });
}

async function resizeImage(
  src: string,
  getTargetSize: (size: Size) => Size | null,
  type: 'image/jpeg' | 'image/png',
  quality?: number
): Promise<string | null> {
  const image = await loadImage(src);
  const target = getTargetSize({ width: image.naturalWidth, height: image.naturalHeight });
  if (!target) return null;

  const canvas = document.createElement('canvas');
  canvas.width = target.width;
  canvas.height = target.height;

  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) return null;

  // Signatures may come with a transparent background; JPEG/opaque canvases would turn it black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, target.width, target.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, target.width, target.height);

  return canvas.toDataURL(type, quality);
}

// Receives and returns the raw base64 (without the data URL prefix), as sent in visitPicture.
// Any failure falls back to the original image so the check-in is never blocked.
export async function optimizeCheckinPicture(base64?: string): Promise<string | undefined> {
  if (!base64) return base64;

  try {
    const optimizedDataUrl = await resizeImage(
      `data:image/jpeg;base64,${base64}`,
      getPictureTargetSize,
      'image/jpeg',
      PICTURE_JPEG_QUALITY
    );
    const optimized = optimizedDataUrl?.split(',')[1];

    return optimized && optimized.length < base64.length ? optimized : base64;
  } catch (error) {
    logger(`[optimizeCheckinPicture] error: ${error}`);
    return base64;
  }
}

// Receives and returns a data URL, as sent in visitNda. Kept as PNG: it is lossless and
// smaller than JPEG for strokes on a plain background.
export async function optimizeSignature(dataUrl?: string): Promise<string | undefined> {
  if (!dataUrl?.startsWith('data:image')) return dataUrl;

  try {
    const optimized = await resizeImage(dataUrl, getSignatureTargetSize, 'image/png');

    return optimized && optimized.length < dataUrl.length ? optimized : dataUrl;
  } catch (error) {
    logger(`[optimizeSignature] error: ${error}`);
    return dataUrl;
  }
}
